/* ═══════════════════════════════════════════════════════════════
   Org Chart V2 — saved-views toolbar segment (People Rebuild
   edit-session grant, 2026-08-17). Picker (SearchableSelect, DESIGN.md
   law: every pick-from-list) + drift indicator + a single "Manage"
   menu (Rename/visibility, Duplicate, Delete) + New/Update/Revert.
   Applying a view is PRESENTATION ONLY (snap/team-boxes/node style) —
   never touches the edit session; reachable in View mode exactly like
   it is mid-session.

   Board switcher (migration 041, Josh grant 2026-08-17: "views become
   separate canvases/boards"): the picker now ALSO switches which
   board's content renders — "Main" plus every visible custom view.
   Every board transition (picker selection, "New view…", deleting the
   active view, Duplicate's post-create switch) routes through the
   caller's `guardSwitch()` first, which blocks (and prompts) while an
   edit session is dirty, or closes a clean (0-dirty) session silently.
   "New view…" creates a genuinely blank board (not just a display-style
   preset) and drops the caller straight into an edit session on it via
   `onViewCreated`.

   Manage menu (Fix 4, Josh incident report 2026-08-17: "Delete/rename
   currently hide inside the edit dialog behind an unlabeled path"):
   ONE obvious icon-button next to the picker replaces both the old
   standalone rename pencil and the delete-button-buried-in-a-dialog-
   footer — Rename/visibility still opens the same edit dialog (now
   rename-only, no delete UI left in it — see SaveViewDialog.tsx), but
   Duplicate runs directly and Delete opens the kit ConfirmDialog
   directly, no extra dialog hop. Gated exactly like the old dialog:
   Rename/Delete need `mayEditActive` (creator or admin, chart-edit
   permission); Duplicate needs chart-edit only and works on Main too
   (Main has no creator, so `mayEditActive` is always false there —
   Main's menu is Duplicate-only, matching the "can't rename/delete
   Main" rule).
   ═══════════════════════════════════════════════════════════════ */

import React, { useEffect, useRef, useState } from 'react';
import { Copy, Pencil, Plus, RotateCcw, Settings2, Trash2 } from 'lucide-react';
import { Icon } from '../../../../components/ui/Icon';
import { rem } from '../../../../lib/rem';
import { SearchableSelect } from '../../../../components/SearchableSelect';
import { Button } from '../../../../components/ui/button';
import { ConfirmDialog } from '../../../../components/ui/confirm-dialog';
import { OrgChartViewService } from '../../../../services/orgChartViewService';
import type { Role } from '../../../../../shared/types';
import type { OrgChartView, OrgChartViewVisibility } from '../../../../../shared/entities';
import type { useOrgChartViewSelection } from '../hooks/useOrgChartViewSelection';
import { SaveViewDialog } from './SaveViewDialog';

export interface SavedViewsBarProps {
  views: OrgChartView[];
  selection: ReturnType<typeof useOrgChartViewSelection>;
  roles: Role[];
  canEdit: boolean;
  currentTeamMemberId?: string;
  isViewAdmin?: boolean;
  refreshAll: () => Promise<void>;
  toast: (msg: string, isError?: boolean) => void;
  /** Board-switch gate (migration 041) — returns whether the caller may
   *  proceed; blocks + toasts while the active board has unsaved edit-
   *  session changes. Applied before the picker switches board, before
   *  "New view…" opens its dialog, before deleting the active view (an
   *  implicit switch to Main), and before Duplicate (which switches to
   *  the new board on success). */
  guardSwitch: () => boolean;
  /** Fired right after a brand-new view row is created and
   *  `activeViewId` has switched to it — the caller drops straight into
   *  an edit session on the (blank) new board. */
  onViewCreated: (viewId: string) => void;
  /** Fix 3 (Josh grant 2026-08-17, "use as a starting point," including
   *  from Main): copies the ACTIVE board (Main or custom) into a new
   *  board and switches to it. Owns its own error toast — this bar just
   *  awaits it for the button's busy state. */
  onDuplicateView: () => Promise<void>;
}

export function SavedViewsBar({
  views,
  selection,
  roles,
  canEdit,
  currentTeamMemberId,
  isViewAdmin,
  refreshAll,
  toast,
  guardSwitch,
  onViewCreated,
  onDuplicateView,
}: SavedViewsBarProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [busy, setBusy] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const manageRef = useRef<HTMLDivElement>(null);

  const activeView = selection.activeView;
  const mayEditActive =
    !!activeView && canEdit && (isViewAdmin || activeView.createdBy === currentTeamMemberId);

  // Manage menu: dismiss on outside click or Esc (law: Esc closes menus).
  useEffect(() => {
    if (!manageOpen) return;
    function onDocMouseDown(e: MouseEvent) {
      if (manageRef.current && !manageRef.current.contains(e.target as Node)) setManageOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setManageOpen(false);
    }
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [manageOpen]);

  async function handleCreate(name: string, visibility: OrgChartViewVisibility) {
    setBusy(true);
    try {
      const now = new Date().toISOString();
      const view: OrgChartView = {
        id: crypto.randomUUID(),
        name,
        config: selection.currentConfig as unknown as Record<string, unknown>,
        visibility,
        createdBy: currentTeamMemberId,
        createdAt: now,
        updatedAt: now,
      };
      await OrgChartViewService.save(view);
      await refreshAll();
      selection.markAppliedConfig(view.id, selection.currentConfig);
      // Switches the caller into an edit session on this (blank) new
      // board — see OrgChartV2.handleCreateBoard.
      onViewCreated(view.id);
      toast(`"${name}" board created`);
      setDialogOpen(false);
    } catch (err: any) {
      toast(err.message || 'Failed to create view', true);
    } finally {
      setBusy(false);
    }
  }

  async function handleEditSubmit(name: string, visibility: OrgChartViewVisibility) {
    if (!activeView) return;
    setBusy(true);
    try {
      const updated: OrgChartView = {
        ...activeView,
        name,
        visibility,
        config: selection.currentConfig as unknown as Record<string, unknown>,
        updatedAt: new Date().toISOString(),
      };
      await OrgChartViewService.save(updated);
      await refreshAll();
      selection.markAppliedConfig(updated.id, selection.currentConfig);
      toast(`View "${name}" updated`);
      setDialogOpen(false);
    } catch (err: any) {
      toast(err.message || 'Failed to update view', true);
    } finally {
      setBusy(false);
    }
  }

  async function handleQuickUpdate() {
    if (!activeView) return;
    setBusy(true);
    try {
      const updated: OrgChartView = {
        ...activeView,
        config: selection.currentConfig as unknown as Record<string, unknown>,
        updatedAt: new Date().toISOString(),
      };
      await OrgChartViewService.save(updated);
      await refreshAll();
      selection.markAppliedConfig(updated.id, selection.currentConfig);
      toast('View updated');
    } catch (err: any) {
      toast(err.message || 'Failed to update view', true);
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!activeView) return;
    // Deleting the ACTIVE view is an implicit switch to Main once it's
    // gone — same dirty-session guard as any other board switch.
    if (!guardSwitch()) return;
    setBusy(true);
    try {
      await OrgChartViewService.delete(activeView.id);
      selection.clearActive();
      await refreshAll();
      toast(`"${activeView.name}" board deleted`);
    } catch (err: any) {
      toast(err.message || 'Failed to delete view', true);
    } finally {
      setBusy(false);
    }
  }

  async function handleDuplicateClick() {
    setManageOpen(false);
    setBusy(true);
    try {
      await onDuplicateView();
    } finally {
      setBusy(false);
    }
  }

  const options = [
    { value: '', label: 'Main' },
    ...views.map((v) => ({ value: v.id, label: v.name })),
  ];

  const menuItemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-2)',
    width: '100%',
    textAlign: 'left',
    padding: rem(7) + ' ' + rem(14),
    fontSize: 'var(--font-size-base)',
    fontWeight: 500,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--tx)',
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: rem(6) }}>
      <span
        style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--tm)',
          fontWeight: 600,
          flexShrink: 0,
        }}
      >
        Board:
      </span>
      <div style={{ width: rem(160) }}>
        <SearchableSelect
          value={selection.activeViewId || ''}
          onChange={(id) => {
            if (id === (selection.activeViewId || '')) return;
            if (!guardSwitch()) return;
            selection.selectView(id || null);
          }}
          options={options}
          placeholder="Main"
          searchPlaceholder="Search boards…"
          clearable={false}
          style={{
            minHeight: 'var(--space-6)',
            padding: 'var(--space-1) var(--space-2)',
            fontSize: 'var(--font-size-sm)',
          }}
        />
      </div>

      {selection.isModified && (
        <>
          <span
            style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)', fontStyle: 'italic' }}
          >
            (modified)
          </span>
          {mayEditActive && (
            <Button variant="outline" size="sm" disabled={busy} onClick={handleQuickUpdate}>
              Update view
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5"
            title="Discard changes and restore this view's saved look"
            aria-label="Revert to saved view"
            onClick={selection.revert}
          >
            <Icon icon={RotateCcw} em={1.2} aria-hidden="true" />
            Revert
          </Button>
        </>
      )}

      {canEdit && (
        <div ref={manageRef} style={{ position: 'relative' }}>
          <Button
            variant="ghost"
            size="icon"
            title="Manage this view"
            aria-label="Manage this view"
            aria-haspopup="menu"
            aria-expanded={manageOpen}
            disabled={busy}
            onClick={() => setManageOpen((v) => !v)}
          >
            <Icon icon={Settings2} em={1.0769} aria-hidden="true" />
          </Button>
          {manageOpen && (
            <div
              role="menu"
              aria-label="View actions"
              style={{
                position: 'absolute',
                left: 0,
                top: 'calc(100% + var(--space-1))',
                background: 'var(--sf)',
                border: '1px solid var(--bd)',
                borderRadius: rem(8),
                boxShadow: '0 8px 24px rgba(0,0,0,.18)',
                padding: 'var(--space-1) 0',
                minWidth: rem(200),
                zIndex: 20,
              }}
            >
              {mayEditActive && (
                <button
                  role="menuitem"
                  style={menuItemStyle}
                  onClick={() => {
                    setManageOpen(false);
                    setDialogMode('edit');
                    setDialogOpen(true);
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--sa)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                >
                  <Icon icon={Pencil} em={1.0769} aria-hidden="true" />
                  Rename or rescope…
                </button>
              )}
              <button
                role="menuitem"
                style={menuItemStyle}
                disabled={busy}
                onClick={handleDuplicateClick}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--sa)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
              >
                <Icon icon={Copy} em={1.0769} aria-hidden="true" />
                Duplicate
              </button>
              {mayEditActive && (
                <>
                  <div style={{ height: rem(1), background: 'var(--bd)', margin: rem(3) + ' 0' }} />
                  <button
                    role="menuitem"
                    style={{ ...menuItemStyle, color: 'var(--dg)' }}
                    onClick={() => {
                      setManageOpen(false);
                      setDeleteConfirmOpen(true);
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--sa)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                  >
                    <Icon icon={Trash2} em={1.0769} aria-hidden="true" />
                    Delete view…
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {canEdit && (
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5"
          title="Start a new, blank board — its own people, lines, and notes, separate from Main"
          aria-label="New view"
          onClick={() => {
            if (!guardSwitch()) return;
            setDialogMode('create');
            setDialogOpen(true);
          }}
        >
          <Icon icon={Plus} em={1.3} aria-hidden="true" />
          New view…
        </Button>
      )}

      <SaveViewDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        mode={dialogMode}
        initialView={dialogMode === 'edit' ? activeView : undefined}
        roles={roles}
        busy={busy}
        onSubmit={dialogMode === 'edit' ? handleEditSubmit : handleCreate}
      />

      <ConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title={`Delete "${activeView?.name ?? 'this'}" board?`}
        description="Every position, line, junction, and note placed on it is deleted too — this can't be undone."
        confirmLabel="Delete view"
        destructive
        busy={busy}
        onConfirm={async () => {
          await handleDelete();
          setDeleteConfirmOpen(false);
        }}
      />
    </div>
  );
}
